import {
  DECIMAL_ROUNDING_CONTRACT_VERSION,
  MAX_DECIMAL_OUTPUT_SCALE,
  roundDecimalQuantityHalfUp,
} from "../../calculator-contracts/decimal";
import {
  validateSeasonalEvidence,
  type SeasonalEvidenceBlocker,
  type SeasonalEvidenceValidationResult,
} from "./schema";
import {
  resolveSeasonalEvidence,
  type SeasonalEvidenceRegistry,
  type SeasonalEvidenceResolutionBlocker,
} from "./source-registry";

/**
 * NL-010 demand-only calculation boundary.
 *
 * This module intentionally has no Funnel Planner, revenue, provider, clock,
 * or network integration. A future owner-approved integration must opt into it
 * explicitly and supply either validated evidence directly or a caller-owned
 * curated registry.
 */
export const SEASONAL_DEMAND_ENGINE_CONTRACT_VERSION = "nl010-seasonal-demand-v1";

/** The literal disabled result required by the NL-010 plan. */
export const SEASONAL_DEMAND_DISABLED_MULTIPLIER = "1.00" as const;

export const SEASONAL_DEMAND_SCENARIOS = ["lower", "expected", "upper"] as const;

export type SeasonalDemandScenario = (typeof SEASONAL_DEMAND_SCENARIOS)[number];

/** Revenue is intentionally absent from this demand-only boundary. */
export const SEASONAL_DEMAND_UNITS = ["visitors", "leads"] as const;

export type SeasonalDemandUnit = (typeof SEASONAL_DEMAND_UNITS)[number];

export type SeasonalDemandQuantity = {
  readonly value: string;
  readonly unit: SeasonalDemandUnit;
};

export type SeasonalManualDemandAdjustment = {
  /** Required label makes this a visible user-owned assumption, not evidence. */
  readonly label: "user-assumption";
  /** Additive demand adjustment; this cannot introduce a second multiplier. */
  readonly demandDelta: SeasonalDemandQuantity;
};

export type SeasonalExistingMultipliers = {
  /** Any supplied property is an attempted second seasonality application. */
  readonly seasonal?: unknown;
  /** Revenue multipliers are expressly outside this demand-only boundary. */
  readonly revenue?: unknown;
};

export type SeasonalDemandCalculationInput = {
  readonly enabled: boolean;
  /** Caller-supplied canonical UTC calendar date; the engine never reads a clock. */
  readonly asOfDate: string;
  readonly geography: string;
  readonly serviceCategory: string;
  /** The calendar month (1–12) being planned; selects the monthly evidence record. */
  readonly plannedMonth: number;
  readonly baselineDemand: SeasonalDemandQuantity;
  readonly scenario: SeasonalDemandScenario;
  readonly outputScale: number;
  /** A caller-owned evidence candidate. It is always revalidated before use. */
  readonly evidence?: unknown;
  /** A caller-owned curated registry. Omitted means the intentionally empty default. */
  readonly registry?: SeasonalEvidenceRegistry;
  readonly manualAdjustment?: SeasonalManualDemandAdjustment;
  readonly existingMultipliers?: SeasonalExistingMultipliers;
};

export type SeasonalDemandEngineBlocker =
  | "invalid-enabled-state"
  | "invalid-baseline-demand"
  | "unsupported-demand-unit"
  | "negative-baseline-demand"
  | "unsupported-seasonal-scenario"
  | "invalid-output-scale"
  | "invalid-manual-adjustment"
  | "manual-adjustment-unit-mismatch"
  | "manual-adjustment-results-in-negative-demand"
  | "conflicting-evidence-input"
  | "invalid-existing-multipliers"
  | "seasonal-multiplier-already-applied"
  | "revenue-multiplier-not-supported"
  | "invalid-seasonal-multiplier"
  | SeasonalEvidenceBlocker
  | `source-registry-${SeasonalEvidenceResolutionBlocker}`;

export type SeasonalDemandCalculationResult = {
  readonly contractVersion: typeof SEASONAL_DEMAND_ENGINE_CONTRACT_VERSION;
  readonly decimalRoundingContractVersion: typeof DECIMAL_ROUNDING_CONTRACT_VERSION;
  readonly readiness: "disabled" | "ready" | "blocked";
  readonly blockers: readonly SeasonalDemandEngineBlocker[];
  /** Never compounded with an existing seasonal or revenue multiplier. */
  readonly multiplier: string;
  /** Null on any enabled failure, which keeps the calculation fail closed. */
  readonly adjustedDemand: SeasonalDemandQuantity | null;
  readonly scenario: SeasonalDemandScenario | null;
  /** Contains no raw adjustment values or caller-entered labels. */
  readonly manualAdjustment: {
    readonly status: "not-supplied" | "applied" | "blocked";
    readonly label: "user-assumption" | null;
  };
  /** Validation metadata only; raw evidence is deliberately not returned. */
  readonly evidenceValidation: SeasonalEvidenceValidationResult | null;
};

type UnknownRecord = Readonly<Record<string, unknown>>;

type ExactDecimal = {
  /** Absolute integer digits; the decimal point is placed by `scale`. */
  readonly digits: string;
  readonly negative: boolean;
  readonly scale: number;
};

type ParsedDemandQuantity = {
  readonly quantity: SeasonalDemandQuantity;
  readonly decimal: ExactDecimal;
};

const DECIMAL_VALUE = /^([+-]?)(\d+)(?:\.(\d+))?$/;

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function hasOwn(value: UnknownRecord | null, property: string): boolean {
  return value !== null && Object.prototype.hasOwnProperty.call(value, property);
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function asDemandUnit(value: unknown): SeasonalDemandUnit | null {
  return typeof value === "string" && (SEASONAL_DEMAND_UNITS as readonly string[]).includes(value)
    ? (value as SeasonalDemandUnit)
    : null;
}

function asScenario(value: unknown): SeasonalDemandScenario | null {
  return typeof value === "string" && (SEASONAL_DEMAND_SCENARIOS as readonly string[]).includes(value)
    ? (value as SeasonalDemandScenario)
    : null;
}

function isValidOutputScale(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_DECIMAL_OUTPUT_SCALE
  );
}

/** Parses a plain decimal string without evaluating it as a JavaScript number. */
function parseExactDecimal(value: unknown): ExactDecimal | null {
  const normalized = nonEmptyString(value);
  const match = normalized ? DECIMAL_VALUE.exec(normalized) : null;

  if (!match) return null;

  const digits = normalizeUnsignedDigits(`${match[2]}${match[3] ?? ""}`);

  return {
    digits,
    negative: match[1] === "-" && digits !== "0",
    scale: (match[3] ?? "").length,
  };
}

function normalizeUnsignedDigits(value: string): string {
  const normalized = value.replace(/^0+(?=\d)/, "");
  return normalized.length > 0 ? normalized : "0";
}

function compareUnsignedDigits(left: string, right: string): number {
  const normalizedLeft = normalizeUnsignedDigits(left);
  const normalizedRight = normalizeUnsignedDigits(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return normalizedLeft.length < normalizedRight.length ? -1 : 1;
  }
  if (normalizedLeft === normalizedRight) return 0;

  return normalizedLeft < normalizedRight ? -1 : 1;
}

function addUnsignedDigits(left: string, right: string): string {
  let leftIndex = left.length - 1;
  let rightIndex = right.length - 1;
  let carry = 0;
  let result = "";

  while (leftIndex >= 0 || rightIndex >= 0 || carry > 0) {
    const leftDigit = leftIndex >= 0 ? Number(left[leftIndex]) : 0;
    const rightDigit = rightIndex >= 0 ? Number(right[rightIndex]) : 0;
    const sum = leftDigit + rightDigit + carry;

    result = `${sum % 10}${result}`;
    carry = Math.floor(sum / 10);
    leftIndex -= 1;
    rightIndex -= 1;
  }

  return normalizeUnsignedDigits(result);
}

/** Subtracts right from left; callers must ensure left is greater than or equal to right. */
function subtractUnsignedDigits(left: string, right: string): string {
  let leftIndex = left.length - 1;
  let rightIndex = right.length - 1;
  let borrow = 0;
  let result = "";

  while (leftIndex >= 0) {
    let digit = Number(left[leftIndex]) - borrow;
    const rightDigit = rightIndex >= 0 ? Number(right[rightIndex]) : 0;

    if (digit < rightDigit) {
      digit += 10;
      borrow = 1;
    } else {
      borrow = 0;
    }

    result = `${digit - rightDigit}${result}`;
    leftIndex -= 1;
    rightIndex -= 1;
  }

  return normalizeUnsignedDigits(result);
}

/**
 * Grade-school multiplication that keeps every intermediate Number below 100.
 * It is therefore exact on ES2017 runtimes and never performs decimal or
 * floating-point arithmetic.
 */
function multiplyUnsignedDigits(left: string, right: string): string {
  if (left === "0" || right === "0") return "0";

  let result = "0";

  for (let rightIndex = right.length - 1, zeroes = 0; rightIndex >= 0; rightIndex -= 1, zeroes += 1) {
    const rightDigit = Number(right[rightIndex]);
    let carry = 0;
    let partial = "";

    for (let leftIndex = left.length - 1; leftIndex >= 0; leftIndex -= 1) {
      const product = Number(left[leftIndex]) * rightDigit + carry;
      partial = `${product % 10}${partial}`;
      carry = Math.floor(product / 10);
    }

    const carried = carry > 0 ? `${carry}${partial}` : partial;
    result = addUnsignedDigits(result, `${carried}${"0".repeat(zeroes)}`);
  }

  return normalizeUnsignedDigits(result);
}

function multiplyExactDecimals(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  const digits = multiplyUnsignedDigits(left.digits, right.digits);

  return {
    digits,
    negative: left.negative !== right.negative && digits !== "0",
    scale: left.scale + right.scale,
  };
}

function addExactDecimals(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  const scale = Math.max(left.scale, right.scale);
  const leftDigits = `${left.digits}${"0".repeat(scale - left.scale)}`;
  const rightDigits = `${right.digits}${"0".repeat(scale - right.scale)}`;

  if (left.negative === right.negative) {
    const digits = addUnsignedDigits(leftDigits, rightDigits);
    return { digits, negative: left.negative && digits !== "0", scale };
  }

  const comparison = compareUnsignedDigits(leftDigits, rightDigits);
  if (comparison === 0) return { digits: "0", negative: false, scale };

  const first = comparison > 0 ? leftDigits : rightDigits;
  const second = comparison > 0 ? rightDigits : leftDigits;
  const digits = subtractUnsignedDigits(first, second);

  return {
    digits,
    negative: comparison > 0 ? left.negative : right.negative,
    scale,
  };
}

function formatExactDecimal(value: ExactDecimal): string {
  const paddedDigits = value.scale > 0 ? value.digits.padStart(value.scale + 1, "0") : value.digits;
  const whole = value.scale === 0 ? paddedDigits : paddedDigits.slice(0, -value.scale);
  const fraction = value.scale === 0 ? "" : paddedDigits.slice(-value.scale);
  const sign = value.negative && value.digits !== "0" ? "-" : "";

  return value.scale === 0 ? `${sign}${whole}` : `${sign}${whole}.${fraction}`;
}

/**
 * The evidence schema deliberately exposes indices as finite numbers. Convert
 * its canonical JavaScript serialization to a plain decimal string once, then
 * perform all demand arithmetic below with ES2017-safe digit strings.
 */
function finiteNumberToPlainDecimal(value: number): string | null {
  if (!Number.isFinite(value)) return null;

  const serialized = String(value);
  const match = /^([+-]?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i.exec(serialized);
  if (!match) return null;

  const sign = match[1] === "-" ? "-" : "";
  const whole = match[2];
  const fraction = match[3] ?? "";
  const exponent = Number(match[4] ?? "0");
  const digits = `${whole}${fraction}`;
  const decimalIndex = whole.length + exponent;

  if (decimalIndex <= 0) return `${sign}0.${"0".repeat(-decimalIndex)}${digits}`;
  if (decimalIndex >= digits.length) return `${sign}${digits}${"0".repeat(decimalIndex - digits.length)}`;

  return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
}

function parseDemandQuantity(value: unknown, blockers: SeasonalDemandEngineBlocker[]): ParsedDemandQuantity | null {
  const candidate = asRecord(value);
  const decimal = parseExactDecimal(candidate?.value);
  const unit = asDemandUnit(candidate?.unit);

  if (!decimal) blockers.push("invalid-baseline-demand");
  if (!unit) blockers.push("unsupported-demand-unit");
  if (!decimal || !unit) return null;
  if (decimal.negative) {
    blockers.push("negative-baseline-demand");
    return null;
  }

  return {
    quantity: { value: nonEmptyString(candidate?.value) as string, unit },
    decimal,
  };
}

function addUnique(
  blockers: SeasonalDemandEngineBlocker[],
  blocker: SeasonalDemandEngineBlocker,
): void {
  if (!blockers.includes(blocker)) blockers.push(blocker);
}

function inspectExistingMultipliers(
  value: unknown,
  blockers: SeasonalDemandEngineBlocker[],
): void {
  if (value === undefined) return;

  const candidate = asRecord(value);
  if (!candidate) {
    addUnique(blockers, "invalid-existing-multipliers");
    return;
  }

  if (hasOwn(candidate, "seasonal")) addUnique(blockers, "seasonal-multiplier-already-applied");
  if (hasOwn(candidate, "revenue")) addUnique(blockers, "revenue-multiplier-not-supported");
}

function emptyManualAdjustment() {
  return { status: "not-supplied" as const, label: null };
}

function blockedManualAdjustment(
  manualAdjustment: ReturnType<typeof parseManualAdjustment>,
  fallback: SeasonalDemandCalculationResult["manualAdjustment"],
): SeasonalDemandCalculationResult["manualAdjustment"] {
  if (!manualAdjustment) return fallback;

  return { status: "blocked", label: manualAdjustment.metadata.label };
}

function blockedResult(
  blockers: SeasonalDemandEngineBlocker[],
  evidenceValidation: SeasonalEvidenceValidationResult | null,
  manualAdjustment: SeasonalDemandCalculationResult["manualAdjustment"],
): SeasonalDemandCalculationResult {
  return {
    contractVersion: SEASONAL_DEMAND_ENGINE_CONTRACT_VERSION,
    decimalRoundingContractVersion: DECIMAL_ROUNDING_CONTRACT_VERSION,
    readiness: "blocked",
    blockers,
    multiplier: SEASONAL_DEMAND_DISABLED_MULTIPLIER,
    adjustedDemand: null,
    scenario: null,
    manualAdjustment,
    evidenceValidation,
  };
}

function disabledResult(
  baselineDemand: SeasonalDemandQuantity | null,
  blockers: SeasonalDemandEngineBlocker[],
): SeasonalDemandCalculationResult {
  return {
    contractVersion: SEASONAL_DEMAND_ENGINE_CONTRACT_VERSION,
    decimalRoundingContractVersion: DECIMAL_ROUNDING_CONTRACT_VERSION,
    readiness: blockers.length === 0 ? "disabled" : "blocked",
    blockers,
    multiplier: SEASONAL_DEMAND_DISABLED_MULTIPLIER,
    adjustedDemand: baselineDemand,
    scenario: null,
    manualAdjustment: emptyManualAdjustment(),
    evidenceValidation: null,
  };
}

function parseManualAdjustment(
  value: unknown,
  baselineUnit: SeasonalDemandUnit,
  blockers: SeasonalDemandEngineBlocker[],
): { readonly decimal: ExactDecimal; readonly metadata: SeasonalDemandCalculationResult["manualAdjustment"] } | null {
  if (value === undefined) return null;

  const candidate = asRecord(value);
  const demandDelta = asRecord(candidate?.demandDelta);
  const decimal = parseExactDecimal(demandDelta?.value);
  const unit = asDemandUnit(demandDelta?.unit);
  const label = candidate?.label;

  if (!candidate || label !== "user-assumption" || !decimal || !unit) {
    addUnique(blockers, "invalid-manual-adjustment");
    return null;
  }
  if (unit !== baselineUnit) {
    addUnique(blockers, "manual-adjustment-unit-mismatch");
    return null;
  }

  return {
    decimal,
    metadata: { status: "applied", label: "user-assumption" },
  };
}

/**
 * Applies one validated seasonal evidence index to non-revenue demand. It is
 * default-off, caller-clocked, and side-effect free. Any unavailable, stale,
 * unlicensed, invalid, scope-mismatched, or double-applied input blocks rather
 * than returning a partially adjusted demand value.
 */
export function calculateSeasonalDemand(input: unknown): SeasonalDemandCalculationResult {
  const candidate = asRecord(input);
  const blockers: SeasonalDemandEngineBlocker[] = [];
  const enabled = candidate?.enabled;
  const baseline = parseDemandQuantity(candidate?.baselineDemand, blockers);

  if (enabled === false) {
    return disabledResult(baseline?.quantity ?? null, blockers);
  }

  if (enabled !== true) {
    addUnique(blockers, "invalid-enabled-state");
    return blockedResult(blockers, null, emptyManualAdjustment());
  }

  inspectExistingMultipliers(candidate?.existingMultipliers, blockers);

  const scenario = asScenario(candidate?.scenario);
  if (!scenario) addUnique(blockers, "unsupported-seasonal-scenario");

  const outputScale = candidate?.outputScale;
  if (!isValidOutputScale(outputScale)) addUnique(blockers, "invalid-output-scale");

  const hasDirectEvidence = hasOwn(candidate, "evidence");
  const hasRegistry = hasOwn(candidate, "registry");
  if (hasDirectEvidence && hasRegistry) addUnique(blockers, "conflicting-evidence-input");

  const registryResolution = hasDirectEvidence
    ? null
    : resolveSeasonalEvidence({
        records: candidate?.registry,
        geography: candidate?.geography,
        serviceCategory: candidate?.serviceCategory,
        plannedMonth: candidate?.plannedMonth,
      });

  if (registryResolution) {
    for (const blocker of registryResolution.blockers) {
      addUnique(blockers, `source-registry-${blocker}`);
    }
  }

  const evidence = hasDirectEvidence ? candidate?.evidence : registryResolution?.evidence;
  const evidenceValidation = validateSeasonalEvidence({
    evidence,
    geography: typeof candidate?.geography === "string" ? candidate.geography : "",
    serviceCategory: typeof candidate?.serviceCategory === "string" ? candidate.serviceCategory : "",
    plannedMonth: typeof candidate?.plannedMonth === "number" ? candidate.plannedMonth : undefined,
    asOfDate: typeof candidate?.asOfDate === "string" ? candidate.asOfDate : "",
  });

  for (const blocker of evidenceValidation.blockers) addUnique(blockers, blocker);

  const manualAdjustment = baseline
    ? parseManualAdjustment(candidate?.manualAdjustment, baseline.quantity.unit, blockers)
    : candidate?.manualAdjustment === undefined
      ? null
      : (addUnique(blockers, "invalid-manual-adjustment"), null);
  const manualMetadata = manualAdjustment?.metadata ??
    (candidate?.manualAdjustment === undefined
      ? emptyManualAdjustment()
      : { status: "blocked" as const, label: null });

  if (!baseline || !scenario || !isValidOutputScale(outputScale) || evidenceValidation.readiness !== "ready") {
    return blockedResult(
      blockers,
      evidenceValidation,
      blockedManualAdjustment(manualAdjustment, manualMetadata),
    );
  }

  const evidenceRecord = asRecord(evidence);
  const indices = asRecord(evidenceRecord?.indices);
  const sourceIndex = indices?.[scenario];
  const multiplierDecimal =
    typeof sourceIndex === "number" ? parseExactDecimal(finiteNumberToPlainDecimal(sourceIndex)) : null;

  if (!multiplierDecimal) {
    addUnique(blockers, "invalid-seasonal-multiplier");
    return blockedResult(
      blockers,
      evidenceValidation,
      blockedManualAdjustment(manualAdjustment, manualMetadata),
    );
  }

  let adjusted = multiplyExactDecimals(baseline.decimal, multiplierDecimal);
  if (manualAdjustment) adjusted = addExactDecimals(adjusted, manualAdjustment.decimal);

  if (adjusted.negative) {
    addUnique(blockers, "manual-adjustment-results-in-negative-demand");
    return blockedResult(
      blockers,
      evidenceValidation,
      manualAdjustment
        ? { status: "blocked", label: manualAdjustment.metadata.label }
        : { status: "blocked", label: null },
    );
  }

  if (blockers.length > 0) {
    return blockedResult(
      blockers,
      evidenceValidation,
      blockedManualAdjustment(manualAdjustment, manualMetadata),
    );
  }

  const rounded = roundDecimalQuantityHalfUp({
    quantity: { value: formatExactDecimal(adjusted), unit: baseline.quantity.unit },
    outputScale,
  });

  if (rounded.readiness !== "ready" || !rounded.roundedQuantity) {
    addUnique(blockers, "invalid-baseline-demand");
    return blockedResult(blockers, evidenceValidation, manualMetadata);
  }

  return {
    contractVersion: SEASONAL_DEMAND_ENGINE_CONTRACT_VERSION,
    decimalRoundingContractVersion: DECIMAL_ROUNDING_CONTRACT_VERSION,
    readiness: "ready",
    blockers,
    multiplier: formatExactDecimal(multiplierDecimal),
    adjustedDemand: {
      value: rounded.roundedQuantity.value,
      unit: baseline.quantity.unit,
    },
    scenario,
    manualAdjustment: manualMetadata,
    evidenceValidation,
  };
}
